import React, { useEffect, useMemo, useState } from "react";
import {
  Paper,
  Stack,
  Typography,
  Box,
  Chip,
  AvatarGroup,
  Avatar,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DragHandleOutlinedIcon from "@mui/icons-material/DragHandleOutlined";
import SnoozeOutlinedIcon from "@mui/icons-material/SnoozeOutlined";
import SwitchAccountOutlinedIcon from "@mui/icons-material/SwitchAccountOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SnoozeDataAccuracy from "../SettingsDataAccuracy/SettingsDataAccuracy";
import ResolveDataAccuracy from "../ResolveDataAccuracy/ResolveDataAccuracy";
import AssignDataAccuracy from "../AssignDataAccuracy/AssignDataAccuracy";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { setSelectedRowsData } from "../../../../../features/dataAccuracySlice";
import { useDispatch } from "react-redux";
import DataStatusAlert from "../UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import { selectAllUsers } from "../../../../../features/userSlice";
import { mapOwnersToNames } from "../DataAccuracyHelpers/helpers";
import StatusLogDialog from "./StatusLogDialog";

interface TaskHeaderCardProps {
  title: string;
  description?: string;
  priority?: string;
  websites?: string[];
  category?: string;
  extraDescription?: string;
}

const TaskHeaderCard: React.FC<TaskHeaderCardProps> = ({
  title,
  description,
  priority,
  websites = [],
  category,
  extraDescription,
}) => {
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [statusLogDialogOpen, setStatusLogDialogOpen] = useState(false);
  const [selectedStatusLog, setSelectedStatusLog] = useState<any[]>([]);
  const [searchParams] = useSearchParams();
  const handleOpenSnooze = () => setSnoozeDialogOpen(true);
  const handleCloseSnooze = () => setSnoozeDialogOpen(false);
  const handleOpenResolve = () => setResolveDialogOpen(true);
  const handleCloseResolve = () => setResolveDialogOpen(false);
  const handleOpenAssign = () => setAssignDialogOpen(true);
  const handleCloseAssign = () => setAssignDialogOpen(false);
  const dispatch = useDispatch();
  const referanceId = searchParams.get("reference_id");
  const { tasksByTaskStructureSharedUid } = useSelector(
    (state: any) => state.dataAccuracy,
  );
  const filteredTasksByTaskStructureSharedUid = useMemo(
    () =>
      (tasksByTaskStructureSharedUid ?? []).filter(
        (task: any) => task.reference_id === referanceId,
      ),
    [tasksByTaskStructureSharedUid, referanceId],
  );

  const {
    current_status = "",
    assignee = "",
    status_log,
  } = filteredTasksByTaskStructureSharedUid[0] ?? {};

  const allUsers = useSelector(selectAllUsers);
  const asigneeName = mapOwnersToNames(assignee, allUsers);

  const handleOpenStatusLog = (statusLog: any[]) => {
    setSelectedStatusLog(statusLog || []);
    setStatusLogDialogOpen(true);
  };

  const handleCloseStatusLog = () => {
    setStatusLogDialogOpen(false);
    setSelectedStatusLog([]);
  };

  useEffect(() => {
    if (filteredTasksByTaskStructureSharedUid.length > 0) {
      dispatch(setSelectedRowsData(filteredTasksByTaskStructureSharedUid));
    }
  }, [dispatch, filteredTasksByTaskStructureSharedUid]);

  return (
    <Paper sx={{ padding: "8px 16px", position: "relative" }}>
      {/* Header Row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
        flexWrap="wrap"
      >
        {/* Left Section — Title, Priority, Chips, Status */}
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{ flexGrow: 1 }}
        >
          {/* Title */}
          <Typography variant="h6" component="h4">
            {title}
          </Typography>

          {/* Priority Icon */}
          {priority && (
            <>
              {priority.toLowerCase() === "high" ? (
                <KeyboardArrowUpIcon sx={{ color: "#D32F2F" }} />
              ) : priority.toLowerCase() === "low" ? (
                <KeyboardArrowDownIcon sx={{ color: "#2196F3" }} />
              ) : priority.toLowerCase() === "lowest" ? (
                <KeyboardDoubleArrowDownIcon sx={{ color: "#2196F3" }} />
              ) : priority.toLowerCase() === "highest" ? (
                <KeyboardDoubleArrowUpIcon sx={{ color: "#D32F2F" }} />
              ) : priority.toLowerCase() === "medium" ? (
                <DragHandleOutlinedIcon sx={{ color: "#ED6C02" }} />
              ) : null}
            </>
          )}

          {/* Website Chips */}
          {websites.map((website, index) => (
            <Chip
              key={index}
              label={website}
              color="secondary"
              size="small"
              sx={{
                height: 22,
                fontSize: "0.75rem",
                borderRadius: "6px",
              }}
            />
          ))}

          {/* Status Chip beside website chips */}
          <Box sx={{ ml: 0.5 }}>
            <DataStatusAlert status={current_status} />
          </Box>

          <Tooltip
            title={
              !status_log || status_log.length === 0
                ? "No status history available"
                : ""
            }
          >
            <span>
              <Button
                data-tracker="639"
                color="secondary"
                disabled={!status_log || status_log.length === 0}
                onClick={() => {
                  handleOpenStatusLog(status_log);
                }}
              >
                Status History log
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {/* Right Section — Actions */}
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8rem", color: "text.secondary" }}
          >
            Actions:
          </Typography>
          <Tooltip title="Snooze">
            <IconButton
              size="small"
              color="secondary"
              onClick={handleOpenSnooze}
              data-tracker="171"
            >
              <SnoozeOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Assign">
            <IconButton
              size="small"
              color="secondary"
              onClick={handleOpenAssign}
              data-tracker="169"
            >
              <SwitchAccountOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Resolve">
            <IconButton
              data-tracker="167"
              size="small"
              color="secondary"
              onClick={handleOpenResolve}
            >
              <CheckCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Query Output Fields (Ordered) */}
      {filteredTasksByTaskStructureSharedUid.length > 0 && (
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 2 }}>
          {Object.entries(filteredTasksByTaskStructureSharedUid[0]?.data || {})
            // Keep only query_output fields
            .filter(([key]) => key.startsWith("query_output"))
            // Sort by 'order' key inside each object
            .sort(
              ([, a]: any, [, b]: any) =>
                (a?.order ?? Number.MAX_SAFE_INTEGER) -
                (b?.order ?? Number.MAX_SAFE_INTEGER),
            )
            // Map and render
            .map(([key, item]: any) => (
              <Typography
                key={key}
                variant="body2"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Typography component="span" sx={{ fontWeight: 500 }}>
                  {item?.title || key.replace("query_output:", "")}:
                </Typography>
                <Typography component="span">{item?.value ?? "—"}</Typography>
              </Typography>
            ))}
        </Box>
      )}

      {/* Second Row — Brand, Category, Assignee */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          fontSize: "14px",
          flexWrap: "wrap",
        }}
      >
        {category && (
          <Typography variant="body2">
            <Typography component="span" sx={{ fontWeight: 500 }}>
              Category:
            </Typography>{" "}
            {category}
          </Typography>
        )}

        {asigneeName.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Assignee:
            </Typography>
            <AvatarGroup
              max={3}
              sx={{ "& .MuiAvatar-root": { width: 24, height: 24 } }}
            >
              {asigneeName.map((owner, index) => (
                <Avatar
                  key={index}
                  alt={owner.toUpperCase()}
                  src="/static/images/avatar/1.jpg"
                />
              ))}
            </AvatarGroup>
            <Typography>{asigneeName.join(", ")}</Typography>
          </Box>
        )}
      </Box>

      {/* Description */}
      {description && (
        <Typography variant="body2" mt={1}>
          {description}
        </Typography>
      )}

      {/* Extra Description */}
      {extraDescription && (
        <Typography variant="body2" mt={0.5} color="text.secondary">
          {extraDescription}
        </Typography>
      )}

      {/* Dialogs */}
      <SnoozeDataAccuracy open={snoozeDialogOpen} onClose={handleCloseSnooze} />
      <ResolveDataAccuracy
        open={resolveDialogOpen}
        onClose={handleCloseResolve}
      />
      <AssignDataAccuracy open={assignDialogOpen} onClose={handleCloseAssign} />

      <StatusLogDialog
        open={statusLogDialogOpen}
        onClose={handleCloseStatusLog}
        statusLog={selectedStatusLog}
      />
    </Paper>
  );
};

export default TaskHeaderCard;
